// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CategoryModel } from "@insite/client-framework/Types/ApiModels";
import Clickable from "@insite/mobius/Clickable";
import Link, { LinkProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import LazyImage from "@sc/MobiusOverrides/LazyImage/LazyImage";
import { color, xScreen } from "@sc/XDL/Utilities";
import { mergeWithCss } from "@sc/XDL/Utilities/mergeWithCss";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    category: any;
    categoryImage: string;
    extendedStyles?: CategoryItemStyles;
}

type Props = OwnProps;

export interface CategoryItemStyles {
    categoryItem?: InjectableCss;
    categoryTitle?: LinkProps;
    imageWrapper?: InjectableCss;
    image?: InjectableCss;
}

export const categoryItemStyles: CategoryItemStyles = {
    categoryItem: {
        css: css`
            justify-content: space-between;
            flex-direction: column;
            display: flex;
        `,
    },
    imageWrapper: {
        css: css`
            justify-content: center;
        `,
    },
    categoryTitle: {
        typographyProps: {
            size: 16,
            lineHeight: "22px",
            weight: "bold",
            css: css`
                margin-bottom: 0;
                &&& {
                    color: ${color("text")};
                }
            `,
        },
        css: css`
            justify-content: center;
            margin-top: 12px;
        `,
    },
    image: {
        css: css`
            width: 204px;
            height: 204px;
            justify-content: center;
            img {
                width: 100%;
                height: auto;
                max-height: 100%;
                object-fit: contain;
            }
        `,
    },
};

const CategoryItem: FC<Props> = ({ category, categoryImage, extendedStyles }) => {
    const [styles] = useState(() => mergeWithCss(categoryItemStyles, extendedStyles));

    if (!category) {
        return null;
    }

    return (
        <StyledWrapper {...styles.categoryItem}>
            <Clickable href={category?.url || category?.path} {...styles.imageWrapper}>
                <LazyImage
                    {...styles.image}
                    src={categoryImage}
                    altText={category.imageAltText}
                    aria-label="category image clickable"
                />
            </Clickable>
            <Link aria-label={category.name} href={category?.url || category?.path} {...styles.categoryTitle}>
                {category.shortDescription ?? category.name}
            </Link>
        </StyledWrapper>
    );
};

export default CategoryItem;
